import { useQuery } from '@tanstack/react-query';

import type { InsuranceV3GetCarriersByStateResponse } from '@/api/generated';
import { insuranceV3GetCarriersByStateOptions } from '@/api/generated/@tanstack/react-query.gen';
import { stateNames } from '@/lib/zipcode.ts';
import {
  CARRIER_TYPE,
  type CarrierType,
  type InsuranceCarrier,
  parseCarrierType,
} from '@/types/insurance.type';

export function useGetInsuranceCarriersQuery(state: string | undefined) {
  const parsedState =
    state && Object.hasOwn(stateNames, state) ? (state as keyof typeof stateNames) : undefined;

  return useQuery({
    ...insuranceV3GetCarriersByStateOptions({ path: { state: parsedState ?? '' } }),
    enabled: !!parsedState,
    select: transformToInsuranceCarriers,
  });
}

const transformToInsuranceCarriers = (
  data: InsuranceV3GetCarriersByStateResponse
): InsuranceCarrier[] => {
  return data
    .flatMap((insurance): InsuranceCarrier[] => {
      const carrierTypes = getCarrierTypesForOption(insurance);

      return carrierTypes.map((type) => ({
        internalId: `${insurance.uuid}-${type}`,
        uuid: insurance.uuid,
        name: insurance.name,
        state: insurance.state,
        carrierType: type,
        displayName: insurance.display_name,
        templateUuid: insurance.template_uuid ?? '',
        isOther: insurance.is_other,
      }));
    })
    .sort((a, b) => {
      const otherSort = Number(a.isOther) - Number(b.isOther);
      if (otherSort !== 0) return otherSort;

      return a.displayName.localeCompare(b.displayName);
    });
};

function getCarrierTypesForOption(
  insurance: InsuranceV3GetCarriersByStateResponse[number]
): CarrierType[] {
  if (insurance.is_other && insurance.carrier_types.length === 0) {
    return [CARRIER_TYPE.MEDICARE_ADVANTAGE, CARRIER_TYPE.MEDICAID_MANAGED_CARE];
  }

  return [
    ...new Set(
      insurance.carrier_types.flatMap((type) => {
        const carrierType = parseCarrierType(type);
        if (!carrierType) return [];

        if (carrierType === CARRIER_TYPE.MEDICARE && insurance.name !== 'Medicare') {
          return [CARRIER_TYPE.MEDICARE_ADVANTAGE];
        }

        if (carrierType === CARRIER_TYPE.MEDICAID && insurance.name !== 'Medicaid') {
          return [CARRIER_TYPE.MEDICAID_MANAGED_CARE];
        }

        return [carrierType];
      })
    ),
  ];
}
